import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const ServiceFeesPanel = () => {
  const [fees, setFees] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/service-fees").then(({ data }) => setFees(data)).catch(() => {});
  }, []);

  if (!fees) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/service-fees", fees);
      setFees(data);
      toast.success("Service fees saved — applied to every Stripe payment from now on");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const example = (100 * (1 + (Number(fees.service_charge_pct) || 0) / 100) + (Number(fees.service_fee_flat) || 0)).toFixed(2);

  return (
    <div className="space-y-4" data-testid="service-fees-panel">
      <p className="text-xs text-zinc-500">
        Added on top of <span className="text-zinc-300">every Stripe card payment</span> across the ecosystem — tournament & league entries,
        memberships, coach sessions, store orders, coin purchases and split payments. Coin (BLC) payments are never charged.
      </p>
      <div className="grid sm:grid-cols-3 gap-3 items-end">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Service Charge (%)</label>
          <input data-testid="service-charge-pct" type="number" min={0} max={25} step={0.1} value={fees.service_charge_pct}
            onChange={(e) => setFees({ ...fees, service_charge_pct: e.target.value })}
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-sm text-white focus:outline-none focus:border-pb-red" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Service Fee (flat $)</label>
          <input data-testid="service-fee-flat" type="number" min={0} max={50} step={0.05} value={fees.service_fee_flat}
            onChange={(e) => setFees({ ...fees, service_fee_flat: e.target.value })}
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-sm text-white focus:outline-none focus:border-pb-red" />
        </div>
        <button data-testid="service-fees-save" onClick={save} disabled={busy}
          className="bg-pb-red text-black font-black uppercase tracking-widest px-5 py-2.5 text-[10px] hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
          {busy ? "Saving…" : "Save Fees"}
        </button>
      </div>
      <div className="text-[11px] text-zinc-500 border border-pb-border bg-pb-elevated/40 px-3 py-2" data-testid="service-fees-example">
        Example: a $100.00 payment charges the player <span className="text-[#A3E635] font-bold">${example}</span>
      </div>
    </div>
  );
};

export default ServiceFeesPanel;
